import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiDatabase, FiTable, FiChevronDown, FiLoader, FiLock } = FiIcons;

const TableSelector = ({
  databases,
  tables,
  selectedDatabase,
  selectedTable,
  onDatabaseChange,
  onTableChange,
  loading,
  readOnly = false
}) => {
  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
        <SafeIcon icon={FiDatabase} className="mr-2 text-blue-500" />
        Database & Tables
      </h2>
      
      {/* Database Display (now read-only) */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
          Database
          {readOnly && (
            <span className="ml-2 text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full flex items-center">
              <SafeIcon icon={FiLock} className="mr-1 w-3 h-3" />
              Locked
            </span>
          )}
        </label>
        
        {readOnly ? (
          <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg flex items-center">
            <SafeIcon icon={FiDatabase} className="mr-2 text-blue-500" />
            <span className="font-medium text-gray-700">{selectedDatabase}</span>
          </div>
        ) : (
          <div className="relative">
            <select
              value={selectedDatabase}
              onChange={(e) => onDatabaseChange(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none bg-white"
              disabled={loading || readOnly}
            >
              <option value="">Choose a database...</option>
              {databases.map((db) => (
                <option key={db} value={db}>
                  {db}
                </option>
              ))}
            </select>
            <SafeIcon
              icon={FiChevronDown}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"
            />
          </div>
        )}
      </div>
      
      {/* Tables List */}
      {selectedDatabase && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-2"
        >
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Tables ({tables.length})
          </label>
          
          {loading ? (
            <div className="flex items-center justify-center py-4">
              <SafeIcon icon={FiLoader} className="animate-spin text-blue-500 mr-2" />
              <span className="text-gray-600">Loading tables...</span>
            </div>
          ) : tables.length > 0 ? (
            <div className="max-h-64 overflow-y-auto space-y-1">
              {tables.map((table) => (
                <motion.button
                  key={table}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => onTableChange(table)}
                  className={`w-full text-left p-3 rounded-lg transition-all flex items-center ${
                    selectedTable === table
                      ? 'bg-blue-100 text-blue-800 border border-blue-300'
                      : 'hover:bg-gray-100 text-gray-700 border border-transparent'
                  }`}
                >
                  <SafeIcon
                    icon={FiTable}
                    className={`mr-2 ${selectedTable === table ? 'text-blue-600' : 'text-gray-400'}`}
                  />
                  <span className="font-medium truncate">{table}</span>
                </motion.button>
              ))}
            </div>
          ) : (
            <div className="text-center py-4 text-gray-500">
              <SafeIcon icon={FiTable} className="mx-auto text-2xl mb-2" />
              <p>No tables found</p>
            </div>
          )}
        </motion.div>
      )}
    </div>
  );
};

export default TableSelector;